import dayjs from '~/timeInit';

import type { GenerateRequest, OfferSchedule, SelectedOffer } from '~/pages/BriefCase/types';

import { SYDNEY_TIMEZONE } from '~/services/TimeService';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const OBJECT_URL_REVOKE_DELAY_MS = 1000;

export function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function triggerBrowserDownload(href: string, filename: string): void {
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

export function downloadBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  triggerBrowserDownload(objectUrl, filename);
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), OBJECT_URL_REVOKE_DELAY_MS);
}

export function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) {
    return 'just now';
  }
  if (mins < 60) {
    return `${mins}m ago`;
  }
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) {
    return `${hrs}h ago`;
  }
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/**
 * BriefCase API expects calendar dates as `YYYY-MM-DD`. The BE already returns
 * dates in that shape (Sydney calendar); pass them through unchanged so we
 * don't shift them via UTC conversion. Only normalise when handed a timestamp
 * string (legacy BQ/SF shapes like `2026-01-19 02:34:09.262+00`), and do so
 * via Sydney rather than UTC.
 */
export function toBriefCaseDateString(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }
  if (ISO_DATE_RE.test(trimmed)) {
    return trimmed;
  }
  const d = dayjs(trimmed);
  if (!d.isValid()) {
    return trimmed;
  }
  return d.tz(SYDNEY_TIMEZONE).format('YYYY-MM-DD');
}

function normalizeOffer(offer: SelectedOffer): SelectedOffer {
  return {
    ...offer,
    offerStartDate: offer.offerStartDate ? toBriefCaseDateString(offer.offerStartDate) : undefined,
    offerEndDate: offer.offerEndDate ? toBriefCaseDateString(offer.offerEndDate) : undefined,
  };
}

function normalizeSchedule(schedule: OfferSchedule): OfferSchedule {
  return {
    startDate: toBriefCaseDateString(schedule.startDate),
    endDate: toBriefCaseDateString(schedule.endDate),
  };
}

export function normalizeGeneratePayload(body: GenerateRequest): GenerateRequest {
  return {
    ...body,
    currentOffer: normalizeOffer(body.currentOffer),
    comparisonOffer: body.comparisonOffer ? normalizeOffer(body.comparisonOffer) : undefined,
    currentSchedule: normalizeSchedule(body.currentSchedule),
    comparisonSchedule: body.comparisonSchedule ? normalizeSchedule(body.comparisonSchedule) : undefined,
  };
}
